import {
  Dispatch,
  SetStateAction,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import debounce from 'lodash.debounce'
import { Autocomplete, MenuItem, TextField } from '@mui/material'
import {
  InfiniteScrollListBox,
  createOnScrollHandler,
} from '@/components/validation/fields/InfiniteScrollListBox'

type LiveListPickerBaseOption = {
  entity_id: string
  pd_display_name: string
}

type LiveListPickerProps<TOption extends LiveListPickerBaseOption> = {
  value?: TOption
  options: TOption[]
  onChange: (entity: TOption | null) => void
  label: string
  searchValue: string
  setSearchValue: Dispatch<SetStateAction<string>>
  isLoading?: boolean
  isFetching?: boolean
  disabled?: boolean
  disableClearable?: boolean
  hasNextPage?: boolean
  fetchNextPage?: () => void
}

export default function LiveListPicker<
  TOption extends LiveListPickerBaseOption,
>({
  value,
  options,
  onChange,
  label,
  searchValue,
  setSearchValue,
  isLoading,
  isFetching,
  disabled,
  disableClearable = true,
  hasNextPage,
  fetchNextPage,
}: LiveListPickerProps<TOption>) {
  const [open, setOpen] = useState(false)
  const [inputValue, setInputValue] = useState<string>('')
  const [isUserSearching, setIsUserSearching] = useState(false)
  const listboxRef = useRef<HTMLUListElement | null>(null)
  const hasScrolledSelectedRef = useRef(false)
  const popperScrollRef = useRef(0)

  useEffect(() => {
    // When closed, show the selected label in the input.
    if (!open) {
      setInputValue(value?.pd_display_name || '')
    }
  }, [open, value?.pd_display_name])

  const debouncedSearch = useMemo(
    () =>
      debounce((newValue: string) => {
        setSearchValue(newValue)
      }, 200),
    [setSearchValue],
  )

  useEffect(() => {
    if (!open) return
    if (!isUserSearching) return
    if (inputValue !== searchValue) {
      debouncedSearch(inputValue)
    }
  }, [debouncedSearch, inputValue, isUserSearching, open, searchValue])

  useEffect(() => {
    return () => {
      debouncedSearch.cancel()
    }
  }, [debouncedSearch])

  useEffect(() => {
    if (!open || hasScrolledSelectedRef.current) return

    const timer = setTimeout(() => {
      const listbox = listboxRef.current
      if (!listbox) return

      const selectedOption = listbox.querySelector<HTMLElement>(
        '[aria-selected="true"]',
      )

      if (selectedOption) {
        selectedOption.scrollIntoView({ block: 'nearest' })
      }

      hasScrolledSelectedRef.current = true
    }, 0)

    return () => clearTimeout(timer)
  }, [open, options.length])

  const onScroll = useMemo(
    () =>
      createOnScrollHandler(
        hasNextPage,
        fetchNextPage,
        popperScrollRef,
        isFetching,
      ),
    [hasNextPage, fetchNextPage, popperScrollRef, isFetching],
  )

  return (
    <div>
      <Autocomplete<TOption, false, true | false>
        autoHighlight
        options={options}
        value={value || null}
        open={open}
        inputValue={inputValue}
        disabled={disabled}
        disableClearable={disableClearable}
        loading={isLoading}
        onOpen={() => {
          setOpen(true)
          setIsUserSearching(false)
          hasScrolledSelectedRef.current = false
          // Start unfiltered on open, while still showing the selected label.
          setSearchValue('')
          setInputValue(value?.pd_display_name || '')
        }}
        onClose={() => {
          setOpen(false)
          setIsUserSearching(false)
          setSearchValue('')
        }}
        onInputChange={(_e, newInputValue, reason) => {
          // Ignore programmatic resets so user-typed filters are not overwritten
          // by the currently selected option label.
          if (reason !== 'input' && reason !== 'clear') return
          setIsUserSearching(true)
          setInputValue(newInputValue)
        }}
        filterOptions={(x) => x}
        renderInput={(params) => {
          return (
            <TextField
              label={label}
              {...params}
              slotProps={{
                inputLabel: {
                  shrink: true,
                },
              }}
              placeholder={isLoading ? 'Loading...' : ''}
            />
          )
        }}
        getOptionLabel={(option) => option?.pd_display_name || 'None'}
        renderOption={(props, option) => {
          if (!option) return null
          return (
            <MenuItem
              {...props}
              key={option.entity_id}
              value={option.entity_id}
            >
              {option.pd_display_name}
            </MenuItem>
          )
        }}
        onChange={(_e, value) => {
          if (!value && disableClearable) return
          onChange(value)
        }}
        slotProps={{
          listbox: {
            onScroll,
            sx: {
              mt: -1,
              pb: 0,
              maxHeight: 'min(40vh, 500px)', // 40vh matches the default max height of the autocomplete, so list resizes when window resizes
            },
            component: InfiniteScrollListBox,
            // @ts-expect-error -- Custom props consumed by InfiniteScrollListBox
            popperScrollRef,
            listboxRef,
            hasNextPage,
            fetchNextPage,
            isFetching,
            optionsLength: options.length,
          },
        }}
      />
    </div>
  )
}
